import { Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { lazyPage } from "./lib/chunkReload";
import { RouteError } from "./pages/RouteError";
import { Loading } from "./components/ui";
import { Home } from "./pages/Home";   // eager: the landing route, so first paint has no chunk wait

// Route-level code splitting — every other page is its own chunk loaded on navigation. Keeps heavy
// per-page deps (notably the xlsx library imported by Import) out of the initial bundle.
const Walkbooks = lazyPage(() => import("./pages/Walkbooks").then((m) => ({ default: m.Walkbooks })));
const WalkbookDetail = lazyPage(() => import("./pages/WalkbookDetail").then((m) => ({ default: m.WalkbookDetail })));
const PhoneListDetail = lazyPage(() => import("./pages/PhoneListDetail").then((m) => ({ default: m.PhoneListDetail })));
const TextSend = lazyPage(() => import("./pages/TextSend").then((m) => ({ default: m.TextSend })));
const GoToTextsSite = lazyPage(() => import("./pages/TextSend").then((m) => ({ default: m.GoToTextsSite })));
const OldMessageLink = lazyPage(() => import("./pages/TextSend").then((m) => ({ default: m.OldMessageLink })));
const NumberGet = lazyPage(() => import("./pages/NumberPages").then((m) => ({ default: m.NumberGet })));
const NumberRemove = lazyPage(() => import("./pages/NumberPages").then((m) => ({ default: m.NumberRemove })));
const NumberHistory = lazyPage(() => import("./pages/NumberPages").then((m) => ({ default: m.NumberHistory })));
const PaidSection = lazyPage(() => import("./pages/PaidSection").then((m) => ({ default: m.PaidSection })));
const PhoneLists = lazyPage(() => import("./pages/Phones").then((m) => ({ default: m.PhoneLists })));
const Cut = lazyPage(() => import("./pages/Cut").then((m) => ({ default: m.Cut })));
const Surveys = lazyPage(() => import("./pages/Surveys").then((m) => ({ default: m.Surveys })));
const SurveyEditor = lazyPage(() => import("./pages/SurveyEditor").then((m) => ({ default: m.SurveyEditor })));
const Universes = lazyPage(() => import("./pages/Universes").then((m) => ({ default: m.Universes })));
const VoterProfile = lazyPage(() => import("./pages/VoterProfile").then((m) => ({ default: m.VoterProfile })));
const HouseholdProfile = lazyPage(() => import("./pages/HouseholdProfile").then((m) => ({ default: m.HouseholdProfile })));
const Tags = lazyPage(() => import("./pages/Tags").then((m) => ({ default: m.Tags })));
const Settings = lazyPage(() => import("./pages/Settings").then((m) => ({ default: m.Settings })));
const Import = lazyPage(() => import("./pages/Import").then((m) => ({ default: m.Import })));
const Integrity = lazyPage(() => import("./pages/Integrity").then((m) => ({ default: m.Integrity })));
const Analytics = lazyPage(() => import("./pages/Analytics").then((m) => ({ default: m.Analytics })));
const Exports = lazyPage(() => import("./pages/Exports").then((m) => ({ default: m.Exports })));
const Coverage = lazyPage(() => import("./pages/PassTracker").then((m) => ({ default: m.PassTracker })));
const Users = lazyPage(() => import("./pages/Users").then((m) => ({ default: m.Users })));
const Signs = lazyPage(() => import("./pages/Signs").then((m) => ({ default: m.Signs })));
const SpotProfile = lazyPage(() => import("./pages/SpotProfile").then((m) => ({ default: m.SpotProfile })));
const SignBookDetail = lazyPage(() => import("./pages/SignBookDetail").then((m) => ({ default: m.SignBookDetail })));
const PollPlan = lazyPage(() => import("./pages/PollPlan").then((m) => ({ default: m.PollPlan })));
const PollPlanNew = lazyPage(() => import("./pages/PollPlanNew").then((m) => ({ default: m.PollPlanNew })));
const Phones = lazyPage(() => import("./pages/Phones").then((m) => ({ default: m.Phones })));
const Account = lazyPage(() => import("./pages/Account").then((m) => ({ default: m.Account })));

const L = (el: ReactNode): ReactNode => <Suspense fallback={<Loading />}>{el}</Suspense>;

export const router = createBrowserRouter([
  {
    path: "/", element: <AppShell />, errorElement: <RouteError />,
    children: [
      { index: true, element: <Home /> },
      { path: "universes", element: L(<Universes />) },
      { path: "voters/:id", element: L(<VoterProfile />) },
      { path: "households/:id", element: L(<HouseholdProfile />) },
      { path: "walkbooks", element: L(<Walkbooks />) },
      { path: "walkbooks/cut", element: L(<Cut />) },
      { path: "walkbooks/:id", element: L(<WalkbookDetail />) },
      // CALLS AND TEXTS (0358): one page, two kinds. `key` so switching between them is a fresh
      // page rather than the same component carrying the other one's filter across.
      { path: "calls", element: L(<Phones key="call" kind="call" />) },
      // THE WALKBOOK CUTTER, cutting call lists (0359). Keyed so moving between the two never carries
      // one kind's filters or pass buckets into the other.
      { path: "calls/new", element: L(<Cut key="call-cut" kind="call" />) },
      { path: "texts", element: L(<Phones key="text" kind="text" />) },
      { path: "texts/new", element: L(<Cut key="text-cut" kind="text" />) },
      // FREE AND PAID ARE TWO SECTIONS (2026-10-23): /calls and /texts open on the choice every time.
      { path: "calls/free", element: L(<PhoneLists key="call-free" kind="call" />) },
      { path: "calls/paid", element: L(<PaidSection key="call-paid" kind="call" />) },
      { path: "texts/free", element: L(<PhoneLists key="text-free" kind="text" />) },
      { path: "texts/paid", element: L(<PaidSection key="text-paid" kind="text" />) },
      { path: "texts/paid/send/:id", element: L(<TextSend />) },
      { path: "texts/paid/send/:id/go/:listId", element: L(<GoToTextsSite />) },
      { path: "texts/paid/messages/:id", element: L(<OldMessageLink />) },
      { path: "texts/setup", element: <Navigate to="/texts/paid?tab=setup" replace /> },
      // THE CAMPAIGN'S NUMBERS (0380): getting one, changing what it's for, removing it, its history.
      { path: "numbers/get", element: L(<NumberGet />) },
      { path: "numbers/:id/remove", element: L(<NumberRemove />) },
      { path: "numbers/:id/history", element: L(<NumberHistory />) },
      // One list's own page, the walkbook detail page's twin (calls/new and texts/new rank first).
      { path: "calls/:id", element: L(<PhoneListDetail key="call-detail" kind="call" />) },
      { path: "texts/:id", element: L(<PhoneListDetail key="text-detail" kind="text" />) },
      // ── NOT IN THE NAV, AND NOW A SECTION OF WALKBOOKS (2026-10-06) ─────────────────────────
      //
      // Coverage used to be reachable two ways with two different idioms: a ghost BUTTON in the
      // Walkbooks header and a TAB on the cutter. It is one tab strip on /walkbooks now, and the
      // cutter is just the cutter.
      //
      // THIS ROUTE STAYS, for the reason it was split in the first place: reaching coverage through
      // the CUTTER instantiates Google Maps, which is the billable event. /walkbooks?tab=coverage
      // and this route both render the tracker with no map; only /walkbooks/cut pays. The route is
      // also a real link somebody may have bookmarked, and it still draws its own title.
      { path: "coverage", element: L(<Coverage />) },
      { path: "surveys", element: L(<Surveys />) },
      { path: "surveys/new", element: L(<SurveyEditor />) },
      { path: "surveys/:id", element: L(<SurveyEditor />) },
      { path: "analytics", element: L(<Analytics />) },
      // EXPORT IS NOT A TAB. Caleb: *"exports are definitely not just a tab in Analytics."* It
      // was not even that -- it was the last card at the bottom of the Charts tab.
      { path: "exports", element: L(<Exports />) },
      { path: "export", element: <Navigate to="/exports" replace /> },
      { path: "live", element: <Navigate to="/analytics?tab=map" replace /> },
      { path: "integrity", element: L(<Integrity />) },
      { path: "reports", element: <Navigate to="/analytics?tab=doors" replace /> },
      { path: "import", element: L(<Import />) },
      { path: "tags", element: L(<Tags />) },
      { path: "users", element: L(<Users />) },
      { path: "signs", element: L(<Signs />) },
      { path: "signs/spot/:placeId", element: L(<SpotProfile />) },
      // A SIGN BOOK HAS ITS OWN PAGE, like a walkbook (2026-09-27). It was a right-hand drawer;
      // see the note at the top of SignBookDetail for why the walkbook layout needs the width.
      { path: "signs/book/:id", element: L(<SignBookDetail />) },
      // POLLING LOCATION PLANS (0411): the list is a tab of Signs; a plan and a new plan are pages.
      { path: "signs/polls/new", element: L(<PollPlanNew />) },
      { path: "signs/polls/:id", element: L(<PollPlan />) },
      // A BOOKMARK FROM THE DAY THIS WAS ITS OWN PAGE still works: Signs opens on the Books tab.
      { path: "sign-books", element: <Navigate to="/signs?tab=books" replace /> },
      { path: "account", element: L(<Account />) },
      { path: "settings", element: L(<Settings />) },
    ],
  },
]);
